import React, { useCallback, useEffect, useState } from 'react'
import { Col, Row, Card, Table, Form, Button, Badge } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie'
import {
  getAllAdmins,
  addAdmin,
  updateAdmin,
  updateAdminStatus,
  resetAdminPassword,
} from '../../../../controller/api/admin'
import { PERMISSION_MODULES, permissionToFlags } from '../../../../helper/adminSession'
import AdminFormModal from '../sub-screen/AdminFormModal'
import ResetPasswordModal from '../sub-screen/ResetPasswordModal'

const PermissionBadges = ({ permission }) => {
  const flags = permissionToFlags(permission)
  return (
    <span>
      {PERMISSION_MODULES.map((mod) =>
        flags[mod.index] === '1' ? (
          <Badge key={mod.index} bg="info" className="me-1">
            {mod.label}
          </Badge>
        ) : null,
      )}
    </span>
  )
}

const TableRow = ({
  row,
  currentEmail,
  activeSuperAdminCount,
  onEdit,
  onResetPassword,
  onStatusChange,
}) => {
  const isSelf = row.email === currentEmail
  const isSuper = row.isSuperAdmin === 1
  const isLastActiveSuperAdmin = isSuper && activeSuperAdminCount <= 1

  return (
    <tr>
      <td>{row.email}</td>
      <td>{row.name || '—'}</td>
      <td>
        <PermissionBadges permission={row.permission} />
      </td>
      <td className="text-center">
        {isSuper ? <Badge bg="warning" text="dark">Super admin</Badge> : '—'}
      </td>
      <td style={{ width: 180 }}>
        <Form.Select
          value={row.status}
          disabled={isLastActiveSuperAdmin}
          onChange={(evt) => onStatusChange(evt, row.id)}
        >
          <option value={0}>Inactive</option>
          <option value={1}>Active</option>
        </Form.Select>
        {isSelf && <small className="text-muted d-block">Tài khoản của bạn</small>}
      </td>
      <td>
        <Button variant="outline-primary" size="sm" className="m-1" onClick={() => onEdit(row)}>
          Sửa
        </Button>
        <Button variant="outline-secondary" size="sm" className="m-1" onClick={() => onResetPassword(row)}>
          Đổi MK
        </Button>
      </td>
    </tr>
  )
}

export const AdminTable = () => {
  const [cookies] = useCookies(['lottmekong'])
  const [list, setList] = useState([])
  const [formModal, setFormModal] = useState({ show: false, mode: 'add', admin: null })
  const [resetModal, setResetModal] = useState({ show: false, admin: null })

  const currentEmail = localStorage.getItem('email')
  const activeSuperAdminCount = list.filter(
    (admin) => admin.isSuperAdmin === 1 && admin.status === 1,
  ).length

  const loadAdmins = useCallback(async () => {
    const response = await getAllAdmins(cookies.lottmekong)
    if (response.error) {
      alert(response.message || 'Không tải được danh sách')
      return
    }
    setList(response.data || [])
  }, [cookies.lottmekong])

  useEffect(() => {
    loadAdmins()
  }, [loadAdmins])

  const onStatusChange = async (evt, id) => {
    const status = parseInt(evt.target.value, 10)
    if (status === 0 && !window.confirm('Vô hiệu hóa tài khoản này? Người dùng sẽ bị đăng xuất.')) {
      loadAdmins()
      return
    }
    const resp = await updateAdminStatus(cookies.lottmekong, id, status)
    if (resp?.error) {
      alert(resp.message || 'Có lỗi xảy ra')
    }
    loadAdmins()
  }

  const handleFormSubmit = async (payload) => {
    if (formModal.mode === 'add') {
      return addAdmin(cookies.lottmekong, payload)
    }
    return updateAdmin(cookies.lottmekong, payload)
  }

  const handleFormHide = (reload) => {
    setFormModal({ show: false, mode: 'add', admin: null })
    if (reload) loadAdmins()
  }

  const handleResetSubmit = async (id, password) => {
    return resetAdminPassword(cookies.lottmekong, id, password)
  }

  const handleResetHide = (reload) => {
    setResetModal({ show: false, admin: null })
    if (reload) loadAdmins()
  }

  return (
    <>
      <Card border="light" className="shadow-sm">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5>Quản trị viên ({list.length})</h5>
            </Col>
            <Col className="text-end">
              <Button
                variant="success"
                onClick={() => setFormModal({ show: true, mode: 'add', admin: null })}
              >
                Thêm quản trị viên
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Table responsive className="align-items-center table-flush">
          <thead className="thead-light">
            <tr>
              <th>Email</th>
              <th>Tên</th>
              <th>Quyền</th>
              <th className="text-center">Vai trò</th>
              <th>Trạng thái</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {list.map((row) => (
              <TableRow
                key={row.id}
                row={row}
                currentEmail={currentEmail}
                activeSuperAdminCount={activeSuperAdminCount}
                onEdit={(admin) => setFormModal({ show: true, mode: 'edit', admin })}
                onResetPassword={(admin) => setResetModal({ show: true, admin })}
                onStatusChange={onStatusChange}
              />
            ))}
          </tbody>
        </Table>
      </Card>

      <AdminFormModal
        show={formModal.show}
        mode={formModal.mode}
        admin={formModal.admin}
        onHide={handleFormHide}
        onSubmit={handleFormSubmit}
      />

      <ResetPasswordModal
        show={resetModal.show}
        admin={resetModal.admin}
        onHide={handleResetHide}
        onSubmit={handleResetSubmit}
      />
    </>
  )
}
